import * as THREE from 'three';
import type { FlyCamera } from './flyCamera';
import { HELP, type Hud } from './hud';
import type { InteriorManager } from './interiorManager';
import type { Shadows } from './shadows';
import type { TileManager } from './tileManager';
import { CAR, CAR_MATERIALS, Car } from './physics/car';
import { type CollisionSource, Physics, usable } from './physics/physics';
import { Walker } from './physics/walker';
import type { PlayerAudio } from './audio/cityAudio';

export type NavMode = 'fly' | 'walk' | 'drive';

const DOWN = new THREE.Vector3(0, -1, 0);
/** Collision streaming radius (m) around the walker, and around the car (plus 2 s of travel). */
const RADIUS = { walk: 40, drive: 55 };
/** Longest wait (ms) for the colliders around a spawn point before placing the player anyway. */
const SPAWN_WAIT = 30000;
/** Search radius (m) for a road to put the car on. */
const ROAD_SEARCH = 45;
/** Chase camera: distance behind, height of the look-at point above the car, default elevation. */
const CHASE = { dist: 7.5, lookUp: 1.3, pitch: -0.3 };
/** The camera's vertical field of view (degrees) at rest; driving widens it with speed. */
const BASE_FOV = 65;

/**
 * Navigation modes (keys 1/2/3): fly (free camera), walk (first-person capsule with collisions,
 * stairs, jumping) and drive (third-person car). Walking and driving load Rapier on first use and
 * stream colliders from the scene around the player (physics/physics.ts). A mode starts at the point
 * below the camera; the car looks for a road nearby.
 */
export class Navigation {
  mode: NavMode = 'fly';
  private phys: Physics | null = null;
  private loading: Promise<Physics> | null = null;
  private walker: Walker | null = null;
  private car: Car | null = null;
  /** The player's car, left where they got out (F gets back in). */
  private parked: Car | null = null;
  /** A mode waiting for its colliders before it can place the player. */
  private spawning: { mode: 'walk' | 'drive'; x: number; y: number; z: number; since: number } | null = null;
  private status = '';
  private lastGround: THREE.Vector3 | null = null;
  private falling = 0;
  private upsideDown = 0;
  private chaseYaw = 0;
  private orbit = 0;
  private lastFlyYaw = 0;
  private orbitIdle = 0;
  private alpha = 1;
  private readonly eye = new THREE.Vector3();
  /** Walking: distance along the stride cycle (for the head bob). */
  private bobPhase = 0;
  private bobAmp = 0;
  /** Kinematic bodies standing in for the traffic cars nearest the player. */
  private readonly trafficBodies: import('@dimforge/rapier3d-compat').RigidBody[] = [];
  /** A car door opened or closed (for the sound). */
  onDoor: (() => void) | null = null;
  /** Called when the mode changes (including falling back to fly when a start fails). */
  onMode: ((mode: NavMode) => void) | null = null;
  /** The player car's model, once the models have loaded (set by the app). */
  carModel: (() => { geometry: THREE.BufferGeometry; material: THREE.Material } | null) | null = null;
  private input = { throttle: 0, steer: 0, handbrake: false };
  private running = false;
  /** Traffic car poses (x, y, z, heading) to collide with; set by the app each frame. */
  trafficPoses: () => [number, number, number, number][] = () => [];

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private readonly fly: FlyCamera,
    private readonly scene: THREE.Scene,
    private readonly tiles: TileManager,
    private readonly interiors: InteriorManager,
    private readonly hud: Hud,
    shadows: Shadows,
  ) {
    window.addEventListener('keydown', (e) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
      if (e.code === 'Digit1') this.setMode('fly');
      if (e.code === 'Digit2') this.setMode('walk');
      if (e.code === 'Digit3') this.setMode('drive');
      if (e.code === 'KeyR' && this.car) this.resetCar();
      if (e.code === 'KeyF') {
        if (this.car && !this.spawning) this.exitCar();
        else if (this.walker && this.nearParked()) this.enterCar();
      }
      // Keep Space from pressing a focused HUD button while walking or driving.
      if (e.code === 'Space' && this.mode !== 'fly') e.preventDefault();
    });
    for (const m of CAR_MATERIALS) shadows.setup(m);
  }

  setMode(mode: NavMode): void {
    if (mode === this.mode && !this.spawning) return;
    this.dropPlayer();
    this.mode = mode;
    this.fly.enabled = mode === 'fly';
    // Walking brings the eye within 35 cm of walls.
    this.camera.near = mode === 'walk' ? 0.12 : 0.3;
    this.camera.fov = BASE_FOV;
    this.camera.updateProjectionMatrix();
    this.hud.setHelp(HELP[mode]);
    this.onMode?.(mode);
    if (mode === 'fly') {
      this.spawning = null;
      this.status = '';
      return;
    }
    const p = this.camera.position;
    this.spawning = { mode, x: p.x, y: p.y, z: p.z, since: performance.now() };
    this.status = 'loading physics…';
    this.loading ??= Physics.load().then((ph) => (this.phys = ph));
    this.loading.catch((err) => {
      console.error(err);
      this.status = 'physics failed to load';
    });
  }

  private dropPlayer(): void {
    this.walker?.dispose();
    this.walker = null;
    if (this.car) {
      this.car.dispose();
      this.car = null;
    }
    if (this.parked) {
      this.parked.dispose();
      this.parked = null;
    }
    this.lastGround = null;
    this.falling = 0;
  }

  private sources(): CollisionSource[] {
    const out: CollisionSource[] = [];
    for (const t of this.tiles.tiles.values()) {
      if (t.terrain && usable(t.terrain)) out.push({ mesh: t.terrain, tag: 'terrain', shape: 'tri' });
      if (!t.city || !usable(t.city)) continue;
      for (const c of t.city.children) {
        if (!(c instanceof THREE.Mesh) || !(c.visible || c.userData.collider)) continue;
        switch (c.name) {
          case 'roads':
          case 'solid':
          case 'ground':
          case 'facade':
            out.push({ mesh: c, tag: c.name, shape: 'tri' });
            break;
          case 'lowWindow':
            out.push({ mesh: c, tag: 'window', shape: 'box' });
            break;
          case 'tree0':
          case 'tree1':
          case 'tree2':
            out.push({ mesh: c, tag: 'tree', shape: 'trunk', radius: 0.22, height: 3 });
            break;
          case 'lamp':
            out.push({ mesh: c, tag: 'lamp', shape: 'trunk', radius: 0.1, height: 6 });
            break;
          case 'roofBox':
          case 'sign':
          case 'awning':
          case 'car0':
          case 'car1':
          case 'car2':
            out.push({ mesh: c, tag: 'solid', shape: 'box' });
            break;
          // Doors are passable; tower windows sit in the (solid) facade.
        }
      }
    }
    for (const f of this.interiors.floors.values()) {
      if (!usable(f.group)) continue;
      for (const c of f.group.children) {
        if (c instanceof THREE.BatchedMesh) out.push({ mesh: c, tag: 'furniture', shape: 'batch' });
        else if (c instanceof THREE.InstancedMesh) {
          // Ceiling fixtures are out of reach and would only snag the head.
          if (!c.name.startsWith('ceiling_')) out.push({ mesh: c, tag: 'furniture', shape: 'box' });
        } else if (c instanceof THREE.Mesh) out.push({ mesh: c, tag: 'interior', shape: 'tri' });
      }
    }
    return out;
  }

  /**
   * Per frame, after the fly camera's update (which applies the mouse look). Moves the camera for the
   * walk and drive modes.
   */
  update(dt: number): void {
    const ph = this.phys;
    if (this.mode === 'fly' || !ph) return;
    if (this.spawning) {
      const s = this.spawning;
      // What the spawn needs: the ground below (walk) or the roads within the search radius (drive).
      const near = s.mode === 'drive' ? ROAD_SEARCH + 6 : 8;
      // The player waits on a spinner meanwhile: a bigger budget than while playing.
      ph.stream(this.sources(), s.x, s.z, RADIUS[s.mode] + (s.mode === 'drive' ? ROAD_SEARCH : 0), 25, near);
      // Let the world see the new colliders before casting rays.
      ph.world.step();
      this.status = `loading colliders… (${ph.pending})`;
      if (ph.pendingNear === 0 || performance.now() - s.since > SPAWN_WAIT) this.trySpawn(ph);
      return;
    }
    if (this.walker) this.updateWalk(ph, dt);
    else if (this.car) this.updateDrive(ph, dt);
  }

  private trySpawn(ph: Physics): void {
    const s = this.spawning!;
    const from = new THREE.Vector3(s.x, s.y, s.z);
    if (s.mode === 'walk') {
      const hit = ph.cast(from, DOWN, 3000);
      if (!hit) return this.giveUp('nothing to stand on below the camera');
      this.walker = new Walker(ph, new THREE.Vector3(s.x, s.y - hit.toi, s.z));
      this.fly.pitch = Math.max(-0.6, Math.min(0.4, this.fly.pitch));
    } else {
      const spot = this.findRoad(ph, from);
      if (!spot) return this.giveUp('no road near the camera');
      const yaw = this.fly.yaw + Math.PI / 2;
      this.car = new Car(ph, spot.add(new THREE.Vector3(0, CAR.wheelRadius + CAR.rest + 0.3, 0)), yaw, this.carModel?.() ?? undefined);
      this.scene.add(this.car.mesh);
      this.chaseYaw = yaw;
      this.orbit = 0;
      this.lastFlyYaw = this.fly.yaw;
      this.fly.pitch = CHASE.pitch;
    }
    this.spawning = null;
    this.status = '';
  }

  private giveUp(why: string): void {
    // Keep trying while colliders are still streaming in, or the city around isn't built yet (slow
    // machines, or right after loading), up to SPAWN_WAIT.
    const waiting = (this.phys?.pending ?? 1) > 0 || [...this.tiles.tiles.values()].some((t) => t.ring <= 1 && !t.city);
    if (performance.now() - this.spawning!.since < SPAWN_WAIT && waiting) return;
    if (performance.now() - this.spawning!.since < 4000) return;
    this.spawning = null;
    this.mode = 'fly';
    this.fly.enabled = true;
    this.camera.near = 0.3;
    this.camera.updateProjectionMatrix();
    this.hud.setHelp(HELP.fly);
    this.status = `can't start: ${why}`;
    this.onMode?.('fly');
  }

  /** The nearest road surface to `from` (rays straight down on rings every 3 m). */
  private findRoad(ph: Physics, from: THREE.Vector3): THREE.Vector3 | null {
    const top = from.y + 30;
    for (let r = 0; r <= ROAD_SEARCH; r += 3) {
      const n = r === 0 ? 1 : Math.ceil((2 * Math.PI * r) / 3);
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        const p = new THREE.Vector3(from.x + Math.cos(a) * r, top, from.z + Math.sin(a) * r);
        const hit = ph.cast(p, DOWN, 3000);
        if (hit?.tag === 'roads' && hit.normal.y > 0.9) return p.setY(top - hit.toi);
      }
    }
    return null;
  }

  private updateWalk(ph: Physics, dt: number): void {
    const w = this.walker!;
    const k = this.fly.keys;
    const f = (k.has('KeyW') ? 1 : 0) - (k.has('KeyS') ? 1 : 0);
    const r = (k.has('KeyD') ? 1 : 0) - (k.has('KeyA') ? 1 : 0);
    const yaw = this.fly.yaw;
    const wish = new THREE.Vector3(-Math.sin(yaw) * f + Math.cos(yaw) * r, 0, -Math.cos(yaw) * f - Math.sin(yaw) * r);
    if (wish.lengthSq() > 1) wish.normalize();
    const run = k.has('ShiftLeft') || k.has('ShiftRight');
    this.running = run;
    const jump = k.has('Space');
    const c = w.cur;
    ph.stream(this.sources(), c.x, c.z, RADIUS.walk, 3);
    this.syncTraffic(ph, c.x, c.z);
    // The parked car keeps its suspension and handbrake while near; farther than the colliders stream
    // it's frozen in place (its ground would be dropped).
    const pk = this.parked;
    if (pk) {
      const near = Math.hypot(pk.position.x - c.x, pk.position.z - c.z) < RADIUS.walk - 8;
      if (pk.body.isEnabled() !== near) pk.body.setEnabled(near);
    }
    const parkedLive = pk && pk.body.isEnabled() ? pk : null;
    this.alpha = ph.advance(
      dt,
      (h) => {
        w.tick(h, wish, run, jump);
        parkedLive?.tick(h, { throttle: 0, steer: 0, handbrake: true });
      },
      () => parkedLive?.capture(),
    );
    parkedLive?.sync(this.alpha);
    // Fell through a gap in the streamed geometry (or off the world): back to the last footing.
    if (w.grounded) {
      this.falling = 0;
      this.lastGround = w.feet;
    } else this.falling += dt;
    if (this.falling > 4 && this.lastGround) {
      w.teleport(this.lastGround.clone().setY(this.lastGround.y + 0.5));
      this.falling = 0;
    } else if (this.falling > 6) {
      this.setMode('fly');
      this.status = 'fell out of the world';
      return;
    }
    this.camera.position.copy(w.eye(this.alpha, this.eye));
    // A slight dip each step (one per 0.78 m walking, 1.35 m running), faded in with speed.
    const stride = run ? 1.35 : 0.78;
    this.bobPhase += (w.speed * dt) / stride;
    this.bobAmp += ((w.grounded ? Math.min(1, w.speed / 3.5) : 0) - this.bobAmp) * Math.min(1, dt * 6);
    this.camera.position.y -= Math.abs(Math.sin(this.bobPhase * Math.PI)) * 0.028 * this.bobAmp * (run ? 1.4 : 1);
  }

  private updateDrive(ph: Physics, dt: number): void {
    const car = this.car!;
    const k = this.fly.keys;
    const input = {
      throttle: (k.has('KeyW') || k.has('ArrowUp') ? 1 : 0) - (k.has('KeyS') || k.has('ArrowDown') ? 1 : 0),
      steer: (k.has('KeyA') || k.has('ArrowLeft') ? 1 : 0) - (k.has('KeyD') || k.has('ArrowRight') ? 1 : 0),
      handbrake: k.has('Space'),
    };
    this.input = input;
    const t = car.body.translation();
    ph.stream(this.sources(), t.x, t.z, RADIUS.drive + Math.min(80, Math.abs(car.speed) * 2), 4);
    this.syncTraffic(ph, t.x, t.z);
    this.alpha = ph.advance(
      dt,
      (h) => car.tick(h, input),
      () => car.capture(),
    );
    car.sync(this.alpha);
    // The view widens a little with speed.
    const fov = BASE_FOV + Math.min(12, Math.abs(car.speed) * 0.4);
    if (Math.abs(this.camera.fov - fov) > 0.05) {
      this.camera.fov += (fov - this.camera.fov) * Math.min(1, dt * 3);
      this.camera.updateProjectionMatrix();
    }
    // Rolled over: put it back on its wheels after a moment.
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(car.mesh.quaternion);
    this.upsideDown = up.y < 0.3 && Math.abs(car.speed) < 2 ? this.upsideDown + dt : 0;
    if (this.upsideDown > 2) this.resetCar();
    this.chase(ph, dt);
  }

  /**
   * Move the kinematic stand-ins onto the 16 traffic cars nearest (x, z) within 40 m, so the walker
   * is blocked and the player's car is pushed. Spare bodies park far below the world.
   */
  private syncTraffic(ph: Physics, x: number, z: number): void {
    const R = ph.R;
    while (this.trafficBodies.length < 16) {
      const b = ph.world.createRigidBody(R.RigidBodyDesc.kinematicPositionBased().setTranslation(0, -1000, 0));
      const c = ph.world.createCollider(R.ColliderDesc.cuboid(2.25, 0.7, 0.92).setTranslation(0, 0.75, 0), b);
      ph.tags.set(c.handle, 'dynamic');
      this.trafficBodies.push(b);
    }
    const near = this.trafficPoses()
      .map((p) => ({ p, d: (p[0] - x) ** 2 + (p[2] - z) ** 2 }))
      .filter((e) => e.d < 40 * 40)
      .sort((a, b) => a.d - b.d)
      .slice(0, this.trafficBodies.length);
    this.trafficBodies.forEach((b, i) => {
      const e = near[i];
      if (!e) {
        b.setNextKinematicTranslation({ x: 0, y: -1000 - i * 10, z: 0 });
        return;
      }
      const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), e.p[3]);
      b.setNextKinematicTranslation({ x: e.p[0], y: e.p[1], z: e.p[2] });
      b.setNextKinematicRotation({ x: q.x, y: q.y, z: q.z, w: q.w });
    });
  }

  private resetCar(): void {
    const car = this.car!;
    car.reset(car.position.clone().add(new THREE.Vector3(0, 1.5, 0)), car.yaw);
    this.upsideDown = 0;
  }

  /** Third-person camera behind the car; the mouse orbits it, easing back behind after a moment. */
  private chase(ph: Physics, dt: number): void {
    const car = this.car!;
    const d = this.fly.yaw - this.lastFlyYaw;
    this.lastFlyYaw = this.fly.yaw;
    if (Math.abs(d) > 1e-5) {
      this.orbit += d;
      this.orbitIdle = 0;
    } else this.orbitIdle += dt;
    if (this.orbitIdle > 1.5) this.orbit *= Math.exp(-dt * 2);
    this.chaseYaw += wrap(car.yaw - this.chaseYaw) * (1 - Math.exp(-dt * 4));
    const yaw = this.chaseYaw - Math.PI / 2 + this.orbit;
    const pitch = Math.max(-1.2, Math.min(0.15, this.fly.pitch));
    const target = car.position.clone().add(new THREE.Vector3(0, CHASE.lookUp, 0));
    const back = new THREE.Vector3(Math.sin(yaw) * Math.cos(pitch), -Math.sin(pitch), Math.cos(yaw) * Math.cos(pitch));
    // Pull in when a wall or ceiling is between the car and the camera.
    let dist = CHASE.dist;
    const hit = ph.cast(target, back, dist + 0.3, car.body);
    if (hit) dist = Math.max(1.2, hit.toi - 0.3);
    this.camera.position.copy(target).addScaledVector(back, dist);
    this.camera.lookAt(target);
  }

  /** Get out on the driver's side (or the other, or behind, whichever is clear) and walk. */
  private exitCar(): void {
    const car = this.car!;
    const ph = this.phys!;
    if (Math.abs(car.speed) > 4) {
      this.flash('slow down to get out');
      return;
    }
    const q = car.mesh.quaternion;
    const from = car.position.clone().add(new THREE.Vector3(0, 0.9, 0));
    const tries = [new THREE.Vector3(0, 0, -1.8), new THREE.Vector3(0, 0, 1.8), new THREE.Vector3(-3.4, 0, 0), new THREE.Vector3(3.4, 0, 0)];
    for (const off of tries) {
      const d = off.clone().applyQuaternion(q);
      d.y = 0;
      const len = d.length();
      if (ph.cast(from, d.clone().normalize(), len + 0.4, car.body)) continue;
      const spot = from.clone().add(d);
      const down = ph.cast(spot.clone().add(new THREE.Vector3(0, 0.5, 0)), DOWN, 4, car.body);
      if (!down) continue;
      car.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
      car.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
      this.car = null;
      this.parked = car;
      car.lights = false;
      this.walker = new Walker(ph, spot.clone().setY(spot.y + 0.5 - down.toi));
      // Face the way the car faces.
      this.fly.yaw = car.yaw - Math.PI / 2;
      this.fly.pitch = -0.05;
      this.switchTo('walk');
      this.onDoor?.();
      return;
    }
    this.flash('no room to get out');
  }

  private enterCar(): void {
    const car = this.parked!;
    this.walker!.dispose();
    this.walker = null;
    this.parked = null;
    car.body.setEnabled(true);
    car.lights = true;
    this.car = car;
    this.chaseYaw = car.yaw;
    this.orbit = 0;
    this.lastFlyYaw = this.fly.yaw;
    this.fly.pitch = CHASE.pitch;
    this.switchTo('drive');
    this.onDoor?.();
  }

  /** Change mode keeping the player where they are (getting in or out of the car). */
  private switchTo(mode: 'walk' | 'drive'): void {
    this.mode = mode;
    this.fly.enabled = false;
    this.camera.near = mode === 'walk' ? 0.12 : 0.3;
    this.camera.fov = BASE_FOV;
    this.camera.updateProjectionMatrix();
    this.hud.setHelp(HELP[mode]);
    this.lastGround = null;
    this.falling = 0;
    this.status = '';
    this.onMode?.(mode);
  }

  private nearParked(): boolean {
    const w = this.walker;
    const p = this.parked;
    return !!w && !!p && Math.hypot(p.position.x - w.cur.x, p.position.z - w.cur.z) < 3.5;
  }

  private flashUntil = 0;
  private flash(text: string): void {
    this.status = text;
    this.flashUntil = performance.now() + 2000;
  }

  /** A one-line prompt for the HUD: getting into the parked car, or a passing message. */
  get prompt(): string {
    if (this.flashUntil && performance.now() > this.flashUntil) {
      this.flashUntil = 0;
      this.status = '';
    }
    if (this.nearParked()) return 'Press F to get in';
    return '';
  }

  /** Things traffic brakes for and pedestrians avoid: the player (with the car's velocity when driving), and their parked car. */
  get obstacles(): { x: number; z: number; vx?: number; vz?: number }[] {
    const out: { x: number; z: number; vx?: number; vz?: number }[] = [];
    const p = this.player;
    if (this.car) {
      // Moving: pedestrians in its way run.
      const v = this.car.body.linvel();
      out.push({ x: this.car.position.x, z: this.car.position.z, vx: v.x, vz: v.z });
    } else if (p) out.push(p);
    if (this.parked) out.push({ x: this.parked.position.x, z: this.parked.position.z });
    return out;
  }

  /** Where the player is when walking or driving (cars brake for it), else null. */
  get player(): { x: number; z: number } | null {
    if (this.walker) return { x: this.walker.cur.x, z: this.walker.cur.z };
    if (this.car) return { x: this.car.position.x, z: this.car.position.z };
    return null;
  }

  /** The player's state for the audio (footsteps, the car's engine, skids and crashes). */
  audioState(): PlayerAudio {
    const w = this.walker;
    const car = this.car;
    const ph = this.phys;
    return {
      walker: w ? { feet: w.feet, grounded: w.grounded, running: this.running && w.speed > 4 } : null,
      car: car
        ? { position: car.position, speed: car.speed, throttle: this.input.throttle, handbrake: this.input.handbrake, lateral: car.lateral, wheels: car.wheelsDown, impact: car.takeImpact() }
        : null,
      ground: (p) => {
        if (!ph) return null;
        const hit = ph.cast(new THREE.Vector3(p.x, p.y + 0.3, p.z), DOWN, 1.5, car?.body);
        return hit ? { tag: hit.tag, y: p.y + 0.3 - hit.toi } : null;
      },
    };
  }

  /** Heading for the map overlay (the fly camera's convention). */
  get heading(): number {
    return this.car ? this.car.yaw - Math.PI / 2 : this.fly.heading;
  }

  /** What navigation is doing or why it couldn't ('' when all is well). */
  get statusText(): string {
    return this.status;
  }

  /** The player car's speed (m/s, signed) while driving, else null. */
  get carSpeed(): number | null {
    return this.car ? this.car.speed : null;
  }

  hudText(): string {
    let s = `mode ${this.mode}`;
    if (this.walker) s += ` · ${this.walker.speed.toFixed(1)} m/s${this.walker.grounded ? '' : ' · airborne'}`;
    if (this.car) s += ` · ${Math.abs(this.car.speed * 3.6).toFixed(0)} km/h`;
    if (this.phys && this.mode !== 'fly') s += ` · ${this.phys.colliderCount} colliders`;
    if (this.status) s += ` · ${this.status}`;
    return s;
  }
}

function wrap(a: number): number {
  return Math.atan2(Math.sin(a), Math.cos(a));
}
